import { FC, Suspense, useEffect, useState } from 'react';
import SingleUnitLeftColumn from './SingleUnitLeftColumn';
import { DropdownItemModel, SkillsCurriculumLessons } from '../../pages/SingleUnit';
import { useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { getSkillProgress, getUnitById } from '../../graphQLOperations/SingleUnit';
import {
  SingleUnitGetTargetQuery,
  SingleUnitGetTargetQuery$data,
} from '../../graphQLOperations/__generated__/SingleUnitGetTargetQuery.graphql';
import SingleUnitRightColumn from './SingleUnitRightColumn';
import { CountPerTypes, SkillsByType } from '../../models/SingleUnit';
import {
  filterOrAddSkillsByLessons,
  singleUnitHelper,
  TargetSkillNode,
} from '../../businessLogicService/getHighAndMediumRelevancesAncestries';
import { filterSkillsByStandards, getStandardsInUnit } from '../../businessLogicService/filterSkillsByStandards';
import { Class } from '../../models/Class';
import { SingleUnitSkillProgressQuery } from '../../graphQLOperations/__generated__/SingleUnitSkillProgressQuery.graphql';
import SingleUnitRightColumnWithProgress from './SingleUnitRightColumnWithProgress';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
interface Props {
  selectedUnit: DropdownItemModel;
  selectedClass: Class[] | [];
  setClassWithoutStudent: (c: boolean) => void;
  selectedLesson?: string[];
  selectedStandards?: string[];
  onStandardsLoaded?: (standards: DropdownItemModel[]) => void;
  skillsCurriculumLessons?: SkillsCurriculumLessons[];
  miamiBrand: boolean;
}

const SingleUnitContainer: FC<Props> = ({
  selectedUnit,
  selectedClass,
  setClassWithoutStudent,
  selectedLesson,
  selectedStandards,
  onStandardsLoaded,
  skillsCurriculumLessons,
  miamiBrand,
}) => {
  const [skillsTypesInUnit, setSkillsTypesInUnit] = useState<CountPerTypes>();
  const [skillsInUnit, setSkillsInUnit] = useState<SkillsByType[]>();
  const [skillsInUnitPlain, setSkillsInUnitPlain] = useState<SkillsByType[]>();
  const [targetSkillsIds, setTargetSkillsIds] = useState<string[]>([]);
  const [classesQueryLoaded, setClassesQueryLoaded] = useState<boolean>(false);
  const [keyProp, setKeyProp] = useState<number>(0);

  const [queryReference, loadQuery] = useQueryLoader<SingleUnitSkillProgressQuery>(getSkillProgress);
  let allSkillsByUnit: SingleUnitGetTargetQuery$data | null = null;
  if (selectedUnit.value) {
    allSkillsByUnit = useLazyLoadQuery<SingleUnitGetTargetQuery>(getUnitById, {
      targetId: selectedUnit?.value,
    });
  }

  useEffect(() => {
    if (selectedClass.length > 0 && selectedUnit) {
      loadQuery(
        {
          classIds: [selectedClass[0].id],
          targetIds: [selectedUnit.value],
        },
        { fetchPolicy: 'network-only' },
      );
      setClassesQueryLoaded(true);
      setKeyProp(new Date().getTime());
    }

    if (selectedClass.length === 0 && skillsInUnitPlain) {
      setClassesQueryLoaded(false);
      setSkillsInUnit(skillsInUnitPlain);
    }
    setClassWithoutStudent(false);
  }, [selectedClass, selectedUnit]);

  useEffect(() => {
    onStandardsLoaded?.(getStandardsInUnit(allSkillsByUnit));
  }, [allSkillsByUnit, onStandardsLoaded]);

  useEffect(() => {
    if (allSkillsByUnit && allSkillsByUnit.target) {
      let data;
      const scopedToStandards = filterSkillsByStandards(selectedStandards, allSkillsByUnit);
      if (miamiBrand) {
        //by default or at first entry, the selectedLesson would be undefined. Lesson node added
        //when Lesson dropdown its applied, Lesson node filter
        const filteredOrAdded = filterOrAddSkillsByLessons(
          selectedLesson && selectedLesson?.length ? selectedLesson : undefined,
          scopedToStandards,
          skillsCurriculumLessons ?? [],
        );
        data = singleUnitHelper(filteredOrAdded as TargetSkillNode);
      } else {
        data = singleUnitHelper(scopedToStandards);
      }
      setTargetSkillsIds(data.tsIds);
      setSkillsTypesInUnit(data.countPerTypes);
      setSkillsInUnit(data.dataSetForType);
      setSkillsInUnitPlain(data.dataSetForType);
      if (selectedClass.length && selectedUnit) {
        setKeyProp(new Date().getTime());
        setClassesQueryLoaded(true);
      }
    }
  }, [allSkillsByUnit, selectedLesson, selectedStandards]);

  return (
    <div className="grid">
      <div className="col-3">{skillsTypesInUnit && <SingleUnitLeftColumn skillsTypesInUnit={skillsTypesInUnit} />}</div>
      <div className="col-9">
        {skillsInUnit &&
          (classesQueryLoaded ? (
            <Suspense fallback={<ThreeDotLoader />}>
              <SingleUnitRightColumnWithProgress
                queryReference={queryReference}
                skillsInUnit={skillsInUnit}
                setSkillsInUnit={setSkillsInUnit}
                keyProp={keyProp}
                setClassWithoutStudent={setClassWithoutStudent}
                targetSkillsIds={targetSkillsIds}
              />
            </Suspense>
          ) : (
            <Suspense fallback={<ThreeDotLoader />}>
              <SingleUnitRightColumn skillsInUnit={skillsInUnit} keyProp={keyProp} targetSkillsIds={targetSkillsIds} />
            </Suspense>
          ))}
      </div>
    </div>
  );
};

export default SingleUnitContainer;
